/**
 * training-drawer.js
 */

import React from 'react';
import {
  useRouteMatch,
  useLocation,
  useHistory,
  matchPath,
} from "react-router-dom";
import { paths } from 'utils/routing';
import { Types } from 'utils/constants';
import EventEdit from 'components/training/event/edit';
import EventView from 'components/training/event/view';
import ExerciseContainer from 'components/training/exercise/container';
import HandlerAnswers from 'components/training/answer/handler-answers';
import MenuDrawer from 'components/menu-drawer';
import QuickTrainingForm from '../quick-training/quick-training-form';
import TrainingMenu from './training-menu';
import TrainingWizard from './training-wizard';
import { UserContext } from 'contexts/user/user-context';


export default function TrainingDrawer(props) {
  let location = useLocation();
  let history = useHistory();
  let basePath = useRouteMatch();
  const { user } = React.useContext(UserContext);
  const drawerBasePath = basePath.url + paths.training.base;
  const [wizard, setWizard] = React.useState();
  const [params, setParams] = React.useState({});


  // MENU ROUTES
  const menuRoutes = [
    ...TrainingWizard.getMenuRoutes(basePath.url), {
    component: TrainingMenu,
    path: [
      drawerBasePath + paths.training.pattern
    ]
  }];


  // MOBILE ROUTES
  const mobileRoutes = [{
    component: TrainingMenu,
    path: [
      drawerBasePath + paths.training.menu.path
    ],
  }, {
    component: EventView,
    path: [
      drawerBasePath + paths.training.viewEvent.path
    ],
  },
  ...TrainingWizard.getMobileRoutes(basePath.url)
  ];

  // MOBILE ROUTES BY ROLE
  switch (user.role.id) {    
    // HANDLER
    default:
      mobileRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewAnswer.path,
          drawerBasePath + paths.training.editAnswer.path,
          drawerBasePath + paths.training.viewExercise.path,
          drawerBasePath + paths.training.editExercise.path,
          drawerBasePath + paths.training.addExercise.path,
        ],
      });
      mobileRoutes.push({
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.editEvent.path
        ],
      });
      mobileRoutes.push({
        component: QuickTrainingForm,
        path: [
          drawerBasePath + paths.training.quickTraining.path
        ],
      });
      break;

    // SUPERVISOR
    case Types.Role.SUPERVISOR:
      mobileRoutes.push({
        component: HandlerAnswers,
        path: [
          drawerBasePath + paths.training.viewHandlerAnswer.path,
        ]
      });
      mobileRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewAnswer.path,
          drawerBasePath + paths.training.viewExercise.path,
        ],
      });
      break;

    // TRAINER
    case Types.Role.TRAINER:
      mobileRoutes.push({
        component: HandlerAnswers,
        path: [
          drawerBasePath + paths.training.viewHandlerAnswer.path,
        ]
      });
      mobileRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewAnswer.path,
          drawerBasePath + paths.training.viewExercise.path,
          drawerBasePath + paths.training.editExercise.path,
          drawerBasePath + paths.training.addExercise.path,
        ],
      });
      mobileRoutes.push({
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.editEvent.path
        ],
      });
      break;
  }
  



  // DESKTOP ROUTES
  const desktopRoutes = [{
    component: EventView,
    path: [
      drawerBasePath + paths.training.viewEvent.path
    ],
  },
  ...TrainingWizard.getDesktopRoutes(basePath.url, user)
  ];

  // DESKTOP ROUTES BY ROLE
  switch (user.role.id) {    
    // HANDLER
    default:
    case Types.Role.HANDLER:
      desktopRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewAnswer.path,
          drawerBasePath + paths.training.editAnswer.path,
          drawerBasePath + paths.training.viewExercise.path,
          drawerBasePath + paths.training.editExercise.path,
          drawerBasePath + paths.training.addExercise.path,
        ],
      });
      desktopRoutes.push({
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.editEvent.path
        ],
      });
      break;

    // SUPERVISOR
    case Types.Role.SUPERVISOR:
      desktopRoutes.push({
        component: HandlerAnswers,
        path: [
          drawerBasePath + paths.training.viewHandlerAnswer.path,
          drawerBasePath + paths.training.viewHandlerAnswers.path,
        ]
      });
      desktopRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewExercise.path,
        ],
      });
      break;

    // TRAINER
    case Types.Role.TRAINER:
      desktopRoutes.push({
        component: HandlerAnswers,
        path: [
          drawerBasePath + paths.training.viewHandlerAnswer.path,
          drawerBasePath + paths.training.viewHandlerAnswers.path,
        ]
      });
      desktopRoutes.push({
        component: ExerciseContainer,
        path: [
          drawerBasePath + paths.training.viewExercise.path,
          drawerBasePath + paths.training.editExercise.path,
          drawerBasePath + paths.training.addExercise.path,
        ],
      });
      desktopRoutes.push({
        component: EventEdit,
        path: [
          drawerBasePath + paths.training.editEvent.path
        ],
      });
      break;
  }


  React.useEffect(() => {
    const wizardMatch = matchPath(
      location.pathname,
      drawerBasePath + paths.training.wizardPattern
    );
    const defaultMatch = matchPath(
      location.pathname,
      drawerBasePath + paths.training.pattern
    );

    const newWizard = wizardMatch ? TrainingWizard : null;
    const newParams = wizardMatch?.params || defaultMatch?.params;

    if (newParams?.id !== undefined) {
      newParams.id = parseInt(newParams.id);
    }
    if (newParams?.step !== undefined) {
      newParams.step = parseInt(newParams.step);
    }

    setWizard(newWizard);
    setParams(newParams);

  }, [location, drawerBasePath]);


  const onCloseLocal = () => {
    history.push(basePath.url);
  }


  return (
    <>
      <MenuDrawer
        drawerPath={paths.training.base}
        basePath={basePath.url}
        menuRoutes={menuRoutes}
        desktopRoutes={desktopRoutes}
        mobileRoutes={mobileRoutes}
        onClose={onCloseLocal}
        wizard={wizard}
        params={params}
        title={`${wizard ? 'ADD' : 'EDIT'} TRAINING`}
        recordGroupType={Types.RecordGroup.TRAINING}
      />
    </>
  );
}